import { useContext, useMemo } from 'react';

import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';

import { WithLocalStorage } from '@/components';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { getRowId } from '@/grid/types';
import { PrimaryContext } from '@/services/context';
import { useContextMenu } from '@/utils/context-menu';

import type { ImplantSession } from '../../types';
import { getSessionColumnDefs } from '../../utils/sessionColumnDefs';

type SessionsGridProps = {
  sessions: ImplantSession[];
  onOpenSession: (session: ImplantSession) => void;
};

export const SessionsGrid = ({
  sessions,
  onOpenSession,
}: SessionsGridProps) => {
  const { t } = useTranslation();
  const gridOptions = useDefaultGridOptions();
  const { themeMode } = useContext(PrimaryContext);
  const { getContextMenuItems } = useContextMenu({ idKey: 'sessionId' });

  const columnDefs = useMemo(
    () => getSessionColumnDefs(t, onOpenSession),
    [onOpenSession, t],
  );

  return (
    <div
      className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} h-[400px] w-full`}
    >
      <WithLocalStorage storageKey="on-farm-implant-sessions-grid">
        <AgGridReact
          gridOptions={gridOptions}
          rowData={sessions}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          domLayout="autoHeight"
          suppressRowClickSelection
          rowHeight={60}
          headerHeight={48}
          overlayNoRowsTemplate={t('onFarmImplant.sessions.noSessions')}
          getRowId={getRowId<ImplantSession>((params) => params.data.sessionId)}
          getContextMenuItems={getContextMenuItems}
        />
      </WithLocalStorage>
    </div>
  );
};
